import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Slider } from 'antd';
import { Flex, Button } from 'ui';
import styled from 'styled-components';
import { injectIntl } from 'react-intl';

const Actions = styled(Flex)`
  margin-top: 20px;
  > :first-child {
    margin-right: 5px;
  }
  ${p => p.isColumnFilter ?
    `position: absolute;
    top: -59px;
    right: -8px;`
    : null
  }
`;

const Action = styled(Button)`
  text-transform: uppercase;
`;

const SliderLabel = styled.div`
  font-size: ${p => p.isColumnFilter ? 14 : 16}px;
  ${l => l.isHighlighted && 'font-weight: 700;'}

  i {
    ${l => l.isHighlighted && 'font-weight: 700;'}
  }
`;

const Title = styled.div`
  font-weight: 400;
  font-size: 16px;
  display: inline-block;
  margin-right: 10px;
`;

const ClearButton = styled.span`
  color: ${p => p.theme.colors.theme};
  font-weight: 400;
  text-decoration: underline;
  cursor: pointer;
`;

const StyledSlider = styled(Slider)`
  margin-right: 40px !important;
  margin-left: 15px !important;
  ${p => p.isColumnFilter && 'width: 90%;'}

  .ant-slider-track {
    height: 2px;
    background-color: #0098be;
  }

  .ant-slider-rail {
    height: 3px;
  }

  .ant-slider-mark-text {
    margin: 0 2px;
  }
`;

const StepRangeFilter = ({
  min,
  max,
  activeFilters,
  onChange,
  disabled,
  isColumnFilter,
  allowClear = false,
  needsConfirm = false,
  intl,
  relativeTimingRangeOptions,
  description,
  inActive = false,
}) => {
  const [currentVal, setCurrentVal] = useState([]);

  const getMarks = () => {
    // returns {sliderpos : id} = {100 : +forever}
    if (relativeTimingRangeOptions) {
      const jsonData = {};
      relativeTimingRangeOptions.forEach((o) => {
        if (currentVal) {
          jsonData[o.sliderPosition] = <SliderLabel
            // foreach loop will not work here, because it will always overwrite currentVal[0]
            isHighlighted={
              o.sliderPosition === currentVal[0] ||
              o.sliderPosition === currentVal[1]
            }
            isColumnFilter={isColumnFilter}
          >{(o.id === 'MinusInfinity' || o.id === 'PlusInfinity') && isColumnFilter ?
            `${o.description.charAt(0)}all` : o.description}
          </SliderLabel>;
        } else {
          jsonData[o.sliderPosition] = <SliderLabel>{o.description}</SliderLabel>;
        }
      });
      return jsonData;
    }
    return null;
  };

  const convertIdToMarks = (currentid) => {
    if (relativeTimingRangeOptions && currentid) {
      return relativeTimingRangeOptions?.filter(o => o?.id === currentid)[0]?.sliderPosition;
    }
    return null;
  };

  const minValue = convertIdToMarks(min);
  const maxValue = convertIdToMarks(max);

  useEffect(() => {
    // if no active filter active yet, or it's broken somehow, reset to full range.
    // else if the lowest date is lower than the minvalue, use the minvalue instead.
    // else if the highest date is larger than the maxvalue, use the maxvalue instead.
    // else use the two dates that were set with the filter.
    if (!inActive) {
      if (!activeFilters || !activeFilters.length || activeFilters.length !== 2) {
        setCurrentVal([minValue, maxValue]);
      } else if (convertIdToMarks(activeFilters[0]) < minValue) {
        setCurrentVal([minValue, convertIdToMarks(activeFilters[1])]);
      } else if (convertIdToMarks(activeFilters[1]) > maxValue) {
        setCurrentVal([convertIdToMarks(activeFilters[0]), maxValue]);
      } else {
        setCurrentVal([convertIdToMarks(activeFilters[0]), convertIdToMarks(activeFilters[1])]);
      }
    } else {
      setCurrentVal([minValue, maxValue]);
    }
  }, [activeFilters, inActive]);

  const convertMarksToId = (currentMarks) => {
    const currentValueOfRange = currentMarks?.map(m =>
      relativeTimingRangeOptions?.filter(o => o?.sliderPosition === m)[0]?.id);
    return currentValueOfRange;
  };

  const onRangeChanged = (values) => {
    // Check if the values are the same as already in state.
    // We have to do this check, because a prop change (state change) of the range values,
    // will trigger the onChange of the Slider, and cause a double load.
    if (!values.every((v, i) => v === currentVal[i])) {
      setCurrentVal(values);
    }
  };

  const updateRange = (values) => {
    const formattedValues = convertMarksToId(values);
    onChange(formattedValues);
  };

  const onMouseUp = (values) => {
    updateRange(values);
  };

  const formatter = value => convertMarksToId([value]);

  const showClearButton = () => <>
    {'( '}
    <ClearButton
      onClick={() => activeFilters?.length ?
        onChange([]) : null}
    >
      {intl.formatMessage({ id: 'worklist.filters.CLEAR' })}
    </ClearButton>
    {' )'}
  </>;

  return (
    <>
      {!isColumnFilter &&
        <>
          <Title>{description.toUpperCase()}</Title>
          {allowClear && showClearButton()}
        </>
      }
      <StyledSlider
        marks={getMarks()}
        range={true}
        step={null}
        value={currentVal}
        onChange={onRangeChanged}
        onAfterChange={!needsConfirm ? onMouseUp : null}
        tipFormatter={!needsConfirm ? formatter : null}
        disabled={disabled || inActive}
        isColumnFilter={isColumnFilter}
      />
      {
        allowClear || needsConfirm ?
          <Actions isColumnFilter={isColumnFilter}>
            {allowClear && isColumnFilter ? showClearButton() : null}
            {needsConfirm && <Action
              type='secondary'
              disabled={!currentVal?.length}
              onClick={() => onMouseUp(currentVal)}
            >
              {intl.formatMessage({ id: 'worklist.filters.APPLY' })}
            </Action>
            }
          </Actions> :
          null
      }
    </>
  );
};

export default injectIntl(StepRangeFilter);

StepRangeFilter.propTypes = {
  min: PropTypes.string,
  max: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  activeFilters: PropTypes.array,
  isColumnFilter: PropTypes.bool,
  allowClear: PropTypes.bool,
  needsConfirm: PropTypes.bool,
  intl: PropTypes.object,
  relativeTimingRangeOptions: PropTypes.object.isRequired,
  description: PropTypes.string,
  inActive: PropTypes.bool,
};
